<template>
  <WithLoader :isLoading="isLoading" classType="primary">
    <div class="csv-history-table__wrapper">
      <VDataTable
        v-if="csvHistoryList && csvHistoryList.length > 0"
        classType="primary"
        :headers="tableFields.headers"
        :rows="tableFields.rows"
        :currentSort="sorting"
        @sort="onSort"
        extraClass="csv-history-table"
      >
      </VDataTable>

      <div class="contacts-table__no-data" v-else>
        No CSV uploads yet!
      </div>
    </div>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

// components
import VDataTable from "@/components/VDataTable";
import WithLoader from "@/components/WithLoader";
import VDateTooltiped from "@/components/VDateTooltiped";
import VCopyIcon from "@/components/VCopyIcon";

// mixins
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";
import withCopy from "@/mixins/withCopy";

const DateStatusField = Vue.component("DateStatusField", {
  props: ["status", "date"],

  components: {
    VDateTooltiped
  },

  template: `
     <div class="date-status-field">
        <div class="status-title" :class="{[status]: !!status}">{{status}}</div>
        <VDateTooltiped :date="date"/>
     </div>
  `
});

export default Vue.component("ContactsCSVHistoryTable", {
  mixins: [withNavigate, withToasted, withCopy],

  components: {
    VDataTable,
    WithLoader
  },

  methods: {
    onSort(sorting) {
      this.fetchCsvHistory({
        page: this.currentPage,
        sorting
      });

      this.changeSort(sorting);
    },

    ...mapActions("importHistory", ["changeSort", "fetchCsvHistory"])
  },

  computed: {
    tableFields() {
      return {
        headers: [
          {
            key: "created_at",
            label: "Date/Status",
            sortable: true,
            extraClass: "date-status",
            width: "15%"
          },
          {
            key: "name",
            label: "Name",
            extraClass: "name",
            width: "25%"
          },
          {
            key: "created_at_field",
            label: "Created",
            extraClass: "created-at",
            width: "10%"
          },
          {
            key: "updated_at",
            label: "Updated",
            extraClass: "updated",
            width: "10%"
          },
          {
            key: "total_rejected",
            label: "Rejected",
            extraClass: "stats rejected",
            width: "8%"
          },
          {
            key: "created_strict_validation",
            label: "Strict",
            extraClass: "stats strict",
            width: "8%"
          },
          {
            key: "created_medium_validation",
            label: "Medium",
            extraClass: "stats medium",
            width: "8%"
          },
          {
            key: "created_off_validation",
            label: "Off",
            extraClass: "stats off-valid",
            width: "8%"
          },
          {
            key: "id",
            label: "ID",
            extraClass: "id",
            width: "8%"
          }
        ],
        rows: this.csvHistoryList.map(historyEl => ({
          values: [
            {
              key: "updated_at",
              component: DateStatusField,
              props: {
                date: historyEl.updated_at,
                status: historyEl.status
              },
              extraClass: `updated_at ${historyEl.status}`
            },
            {
              key: "name",
              value: historyEl.name,
              extraClass: "csv-filename"
            },
            {
              key: "created_at",
              value: historyEl.total_created || "N/A"
            },
            {
              key: "updated",
              value: historyEl.total_updated || "N/A"
            },
            {
              key: "total_rejected",
              value: historyEl.total_rejected || "N/A"
            },
            {
              key: "created_strict_validation",
              value: historyEl.created_strict_validation || "N/A"
            },
            {
              key: "created_medium_validation",
              value: historyEl.created_medium_validation || "N/A"
            },
            {
              key: "created_off_validation",
              value: historyEl.created_off_validation || "N/A"
            },
            {
              key: "id",
              component: VCopyIcon,
              props: {
                copyText: historyEl.id
              }
            }
          ]
        }))
      };
    },

    ...mapGetters("importHistory", ["isLoading", "csvHistoryList", "sorting"]),
    ...mapGetters("contacts", ["identityLoading"])
  },

  mounted() {
    if (!this.identityLoading && !this.isLoading) {
      this.fetchCsvHistory({ page: 1, sorting: this.sorting });
    }
  }
});
</script>

<style lang="scss">
$ns: "csv-history-table";

.#{$ns} {
  /deep/ .v-tooltip__wrapper {
    height: $input-height;
    color: $secondary-text-color;
  }

  .v-tooltip__wrapper.v-time-tooltip [tooltip][flow^="up"]::after {
    padding: 0;
  }

  th {
    &.stats {
      span {
        display: block;
        max-width: 87px;
        @include textCropEllipsis();
      }
    }
  }

  td {
    position: relative;
    color: $secondary-text-color;

    &.updated_at {
      padding: 0 10px !important;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 5px;
      }

      &.succeeded {
        &:before {
          background: $success-color;
        }
      }

      &.processing {
        &:before {
          background: #fda85e;
        }
      }

      &.queued {
        &:before {
          background: $primary-grey;
        }
      }

      &.failed {
        &:before {
          background: $danger-color;
        }
      }
    }

    &.csv-filename {
      @include textCropEllipsis();
      max-width: 150px;
      padding-right: 50px;
    }
  }
}

.date-status-field {
  .status-title {
    text-transform: capitalize;
    font-size: 15px;
    height: 24px;
    align-items: center;
    display: flex;
    font-family: Lato-Light, sans-serif;
  }

  .succeeded {
    color: $success-color;
  }

  .processing {
    color: #fda85e;
  }

  .queued {
    color: $primary-grey;
  }

  .failed {
    color: $danger-color;
  }
}
</style>
